import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  IconButton,
  Modal,
  Button,
  Typography,
  InputAdornment,
  Tooltip,
  Alert,
  useTheme,
} from "@mui/material";
import ExpandIcon from "@mui/icons-material/OpenInFull";
import CollapseIcon from "@mui/icons-material/CloseFullscreen";
import CloseIcon from "@mui/icons-material/Close";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Send, AutoAwesome } from "@mui/icons-material";
import ChannelSelector, { INTERNAL_NOTE_CHANNEL_ID } from "./ChannelSelector";
import { getChannelById, getChannelComposerSurfaceSx } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";
import ComposeAttachMenu from "./ComposeAttachMenu";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import FilePreview from "./FilePreview";
import { composerHasSendPayload } from "./composeAttachmentHelpers";
import { composerHasTextPayload, isProbablyHtml, stripHtmlToPlain } from "./composerHtmlUtils";
import {
  describeClientMessage,
  formatCustomerEmailWhen,
  isInboundClientEmail,
  listCustomerEmails,
  parseCustomerEmail,
} from "./emailCompose";
import {
  COMPOSER_MOBILE_ACTION_BUTTON_SIZE_PX,
  COMPOSER_MOBILE_ROW_HEIGHT_PX,
  COMPOSER_ROW_HEIGHT_PX,
  composerMobileInputRowSx,
  composerPrimaryActionButtonSx,
} from "./composerConstants";
import { COMPOSER_MOBILE_TEXT_FIELD_SX, COMPOSER_TEXT_FIELD_SX } from "./composerTextFieldStyles";
import ComposerMobileToolbar from "./ComposerMobileToolbar";

const CUSTOMER_MESSAGE_HIDDEN_KEY = "xpro.email.composer.customerMessageHidden";

function escapeEditorText(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** O Quill só preserva o texto se receber HTML. Texto puro vira parágrafos. */
function toQuillHtml(value) {
  const raw = String(value || "");
  if (!raw.trim()) return "";
  if (isProbablyHtml(raw)) return raw;
  return raw
    .split("\n")
    .map((line) => `<p>${escapeEditorText(line) || "<br>"}</p>`)
    .join("");
}

function readCustomerMessageHidden() {
  try {
    return localStorage.getItem(CUSTOMER_MESSAGE_HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCustomerMessageHidden(hidden) {
  try {
    localStorage.setItem(CUSTOMER_MESSAGE_HIDDEN_KEY, hidden ? "1" : "0");
  } catch {
    /* preferência local é opcional */
  }
}

function CustomerEmailCard({ message, collapsed = false }) {
  const described = describeClientMessage(message);
  const when = formatCustomerEmailWhen(message?.createdAt);
  const who = message?.senderName || "Cliente";
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        {[who, described.channel, when].filter(Boolean).join(" · ")}
      </Typography>
      {described.subject ? (
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, fontWeight: 700, lineHeight: 1.35, wordBreak: "break-word" }}
        >
          {described.subject}
        </Typography>
      ) : null}
      <Typography
        variant="body2"
        sx={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          lineHeight: 1.6,
          fontSize: "0.92rem",
          ...(collapsed
            ? {
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : {}),
        }}
      >
        {described.body || "Esta mensagem não tem texto."}
      </Typography>
    </Box>
  );
}

function CustomerEmailPane({ emails, showAll, onToggleAll, onHide, isMobile }) {
  const theme = useTheme();
  const latest = emails[0];
  const older = emails.slice(1);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const visibleOlder = showAll ? older : [];
  const showBody = !isMobile || mobileOpen || showAll;

  return (
    <Box
      sx={{
        flex: isMobile ? "none" : "0 0 380px",
        width: isMobile ? "100%" : 380,
        maxWidth: isMobile ? "100%" : "40%",
        minWidth: 0,
        minHeight: 0,
        maxHeight: isMobile ? (showBody ? "40dvh" : "none") : "100%",
        overflow: "auto",
        border: "1px solid",
        borderColor: "divider",
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        borderRadius: 2,
        bgcolor: theme.palette.mode === "dark" ? "background.default" : "grey.50",
        px: isMobile ? 1.5 : 2,
        py: isMobile ? 1.25 : 1.75,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, letterSpacing: 0.6 }}>
          {showAll ? "Mensagens do cliente" : "Última mensagem"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <Button size="small" onClick={onHide} sx={{ textTransform: "none", minWidth: 0, px: 0.75, color: "text.secondary" }}>
            Ocultar
          </Button>
          {older.length > 0 ? (
            <Button size="small" onClick={onToggleAll} sx={{ textTransform: "none", minWidth: 0, px: 0.75 }}>
              {showAll ? "Só a última" : `Todas (${emails.length})`}
            </Button>
          ) : null}
          {isMobile ? (
            <Button size="small" onClick={() => setMobileOpen((open) => !open)} sx={{ textTransform: "none", minWidth: 0 }}>
              {showBody ? "Recolher" : "Ler"}
            </Button>
          ) : null}
        </Box>
      </Box>
      <CustomerEmailCard message={latest} collapsed={isMobile && !showBody} />
      {visibleOlder.map((item) => (
        <Box key={item.id} sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <CustomerEmailCard message={item} />
        </Box>
      ))}
    </Box>
  );
}

const MessageEmailChannel = ({
  message,
  setMessage,
  channelType,
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  isMobile,
  isExpanded,
  setIsExpanded,
  file,
  files = [],
  handleRemoveFile,
  selectedChannel,
  setSelectedChannel,
  clientConversation = null,
  conversationMessages = null,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
  /** E-mail do destinatário da solicitação (pré-preenche "Para") */
  recipientEmail = "",
  /** Mensagens da conversa para extrair assunto em respostas (Re:) */
  messages = [],
  emailSendError = "",
  emailSending = false,
  onDismissEmailError,
  onStartVideo,
  videoSending = false,
  videoSendDisabled = false,
  videoSendDisabledHelp = null,
  videoMenuLabel = null,
  onJoinVideo,
  joinVideoSending = false,
  showJoinVideo = false,
  onStartGoogleMeet,
  googleMeetSending = false,
  googleMeetDisabled = false,
  googleMeetDisabledHelp = null,
}) => {
  const inputRef = useRef(null);
  const theme = useTheme();
  const inputBorderRadius = 16;
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const currentChannel = getChannelById(channels, selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const isInternalNote =
    String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID) || channelType === "Xpro";
  const composerSurface = getChannelComposerSurfaceSx(theme, {
    channelType,
    channelName,
    isInternalNote,
  });
  const canUseAiSuggestion =
    !!onGenerateSuggestion && channelType !== "Xpro" && hasHistory;
  const aiDisabled = isGeneratingSuggestion || !hasHistory;

  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [showAllCustomerEmails, setShowAllCustomerEmails] = useState(false);
  const [customerMessageHidden, setCustomerMessageHidden] = useState(readCustomerMessageHidden);
  const subjectEditedRef = useRef(false);
  const quillHydratedRef = useRef(false);

  const customerEmails = useMemo(() => listCustomerEmails(messages), [messages]);
  const customerEmail = customerEmails[0] || null;
  const replyEmail = useMemo(
    () => customerEmails.find((item) => isInboundClientEmail(item)) || null,
    [customerEmails]
  );
  const replyParsed = useMemo(
    () => (replyEmail ? parseCustomerEmail(replyEmail) : null),
    [replyEmail]
  );

  useEffect(() => {
    const to = (recipientEmail && String(recipientEmail).trim()) || "";
    setEmailTo((prev) => (prev === "" && to ? to : prev || to));
  }, [recipientEmail]);

  useEffect(() => {
    if (subjectEditedRef.current || emailSubject) return;
    const sub = replyParsed?.subject
      ? (/^re:/i.test(replyParsed.subject) ? replyParsed.subject : `Re: ${replyParsed.subject}`)
      : "";
    if (sub) setEmailSubject(sub);
  }, [replyParsed, emailSubject]);

  const handleExpand = () => setIsExpanded(true);
  const handleCollapse = () => {
    quillHydratedRef.current = false;
    setIsExpanded(false);
  };

  useEffect(() => {
    if (!isExpanded) quillHydratedRef.current = false;
  }, [isExpanded]);

  const splitCc = () =>
    emailCc.trim() ? emailCc.trim().split(/\s*[,;]\s*/).filter(Boolean) : undefined;

  const onSend = async () => {
    if (emailSending) return;
    const hasContent = composerHasTextPayload(message) || composerHasSendPayload("", file, files, null);
    const to = emailTo.trim() || String(recipientEmail || "").trim();
    if (hasContent && to && typeof handleSendMessage === "function") {
      const result = await handleSendMessage({
        emailTo: to,
        emailCc: splitCc(),
        emailSubject: emailSubject.trim() || undefined,
      });
      if (result && result.ok === false) return;
      handleCollapse();
    }
  };

  const canSend =
    !emailSending &&
    (composerHasTextPayload(message) || composerHasSendPayload("", file, files, null)) &&
    Boolean((emailTo.trim() || String(recipientEmail || "").trim()));
  const showCustomerPane = Boolean(customerEmail) && !customerMessageHidden;
  const toggleCustomerMessage = () => {
    setCustomerMessageHidden((hidden) => {
      const next = !hidden;
      writeCustomerMessageHidden(next);
      return next;
    });
  };
  const fieldDivider = `1px solid ${theme.palette.divider}`;
  const contextLine = [
    (emailTo.trim() || String(recipientEmail || "").trim()) &&
      `Para ${emailTo.trim() || String(recipientEmail || "").trim()}`,
    emailSubject.trim() && `Assunto: ${emailSubject.trim()}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box
        sx={{
        position: "relative", 
        width: "100%",
        display: "flex",
        flexDirection: "column",
        // Mantém alinhado com os demais canais (WhatsApp/SMS/etc).
        paddingBottom: 0,
        }}
    >
      {!isExpanded ? (
        <Box sx={{ width: "100%", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {contextLine ? (
            <Typography
              variant="caption"
              noWrap={!isMobile}
              sx={{
                px: 0.5,
                color: "text.secondary",
                display: "block",
                ...(isMobile
                  ? { whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 }
                  : {}),
              }}
            >
              {contextLine}
            </Typography>
          ) : null}
          {emailSendError ? (
            <Alert severity="warning" onClose={onDismissEmailError} sx={{ py: 0 }}>
              {emailSendError}
            </Alert>
          ) : null}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          gap={isMobile ? 0 : 1}
          sx={{ width: "100%", minWidth: 0 }}
        >
        <Box
          display="flex"
          flexDirection="row"
          alignItems={isMobile ? "flex-end" : "center"}
          gap={isMobile ? 0.5 : 0}
          sx={isMobile ? composerMobileInputRowSx : { width: "100%", minWidth: 0 }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "stretch",
              borderRadius: `${inputBorderRadius}px`,
              overflow: "hidden",
              minHeight: isMobile ? COMPOSER_MOBILE_ROW_HEIGHT_PX : COMPOSER_ROW_HEIGHT_PX,
              ...composerSurface.shell,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                alignSelf: "stretch",
                px: 0.25,
                ...composerSurface.divider,
              }}
            >
              <ChannelSelector
                integrated
                selectedChannel={selectedChannel}
                setSelectedChannel={setSelectedChannel}
                clientConversation={clientConversation}
                conversationMessages={conversationMessages}
              />
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              variant="outlined"
              placeholder={`Envie uma mensagem via ${channelType}`}
              value={stripHtmlToPlain(message || "", { trim: false })}
              onChange={(e) => {
                onDismissEmailError?.();
                setMessage(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (canSend) onSend();
                }
              }}
              InputProps={{
                style: {
                  borderRadius: 0,
                  fontSize: isMobile ? "16px" : "1rem",
                },
                inputRef: inputRef,
                startAdornment: isMobile ? undefined : (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <IconButton onClick={handleEmojiPickerOpen} edge="start" aria-label="Emoji">
                      <InsertEmoticonIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: isMobile ? undefined : (
                  <InputAdornment position="end" sx={{ mr: 0.25, display: "flex", alignItems: "center", gap: 0 }}>
                    {canUseAiSuggestion && (
                      <Tooltip
                        title={
                          !hasHistory
                            ? "Disponível após a conversa ter histórico."
                            : isGeneratingSuggestion
                            ? "Gerando resposta..."
                            : "Sugerir resposta com IA"
                        }
                      >
                        <span>
                          <IconButton onClick={onGenerateSuggestion} disabled={aiDisabled}>
                            <AutoAwesome />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <ComposeAttachMenu
                      openFilePicker={openFilePicker}
                      showAttachProducts={false}
                      showLocation={false}
                      showStartVideo={typeof onStartVideo === "function"}
                      onStartVideo={onStartVideo}
                      videoSending={videoSending}
                      videoSendDisabled={videoSendDisabled}
                      videoSendDisabledHelp={videoSendDisabledHelp}
                      videoMenuLabel={videoMenuLabel}
                      showJoinVideo={showJoinVideo && typeof onJoinVideo === "function"}
                      onJoinVideo={onJoinVideo}
                      joinVideoSending={joinVideoSending}
                      showGoogleMeet={typeof onStartGoogleMeet === "function"}
                      onStartGoogleMeet={onStartGoogleMeet}
                      googleMeetSending={googleMeetSending}
                      googleMeetDisabled={googleMeetDisabled}
                      googleMeetDisabledHelp={googleMeetDisabledHelp}
                      size="medium"
                    />
                    <IconButton onClick={handleExpand}>
                      <ExpandIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        if (canSend) onSend();
                      }}
                      disabled={!canSend}
                      aria-label="Enviar"
                      sx={{
                        ml: 0.25,
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        borderRadius: "50%",
                        p: 1,
                        "&:hover": { backgroundColor: "primary.dark" },
                        "&.Mui-disabled": {
                          backgroundColor: "action.disabledBackground",
                          color: "action.disabled",
                        },
                      }}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={isMobile ? COMPOSER_MOBILE_TEXT_FIELD_SX : COMPOSER_TEXT_FIELD_SX}
            />
          </Box>
          {isMobile ? (
            <ComposerMobileToolbar>
              <IconButton onClick={handleEmojiPickerOpen} size="small" aria-label="Emoji">
                <InsertEmoticonIcon fontSize="small" />
              </IconButton>
              {canUseAiSuggestion ? (
                <Tooltip
                  title={
                    !hasHistory
                      ? "Disponível após a conversa ter histórico."
                      : isGeneratingSuggestion
                      ? "Gerando resposta..."
                      : "Sugerir resposta com IA"
                  }
                >
                  <span>
                    <IconButton onClick={onGenerateSuggestion} disabled={aiDisabled} size="small">
                      <AutoAwesome fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              <ComposeAttachMenu
                openFilePicker={openFilePicker}
                showAttachProducts={false}
                showLocation={false}
                showStartVideo={typeof onStartVideo === "function"}
                onStartVideo={onStartVideo}
                videoSending={videoSending}
                videoSendDisabled={videoSendDisabled}
                videoSendDisabledHelp={videoSendDisabledHelp}
                videoMenuLabel={videoMenuLabel}
                showJoinVideo={showJoinVideo && typeof onJoinVideo === "function"}
                onJoinVideo={onJoinVideo}
                joinVideoSending={joinVideoSending}
                showGoogleMeet={typeof onStartGoogleMeet === "function"}
                onStartGoogleMeet={onStartGoogleMeet}
                googleMeetSending={googleMeetSending}
                googleMeetDisabled={googleMeetDisabled}
                googleMeetDisabledHelp={googleMeetDisabledHelp}
                size="small"
              />
              <Tooltip title="Ampliar editor">
                <IconButton onClick={handleExpand} size="small" aria-label="Ampliar editor">
                  <ExpandIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ComposerMobileToolbar>
          ) : null}
          {isMobile ? (
            <IconButton
              onClick={() => {
                if (canSend) onSend();
              }}
              disabled={!canSend}
              aria-label="Enviar"
              sx={composerPrimaryActionButtonSx({ size: COMPOSER_MOBILE_ACTION_BUTTON_SIZE_PX })}
            >
              <Send fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
        </Box>
        </Box>
      ) : (
        <Modal open={isExpanded} onClose={handleCollapse}>
      <Box
        sx={{
          position: "absolute",
          top: isMobile ? 0 : "50%",
          left: isMobile ? 0 : "50%",
          transform: isMobile ? "none" : "translate(-50%, -50%)",
          width: isMobile ? "100%" : "min(1320px, 96vw)",
          height: isMobile ? "100dvh" : "min(900px, 94vh)",
          maxHeight: isMobile ? "100dvh" : "94vh",
          overflow: "hidden",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: isMobile ? 1.5 : 2.5,
          pb: isMobile ? "max(12px, env(safe-area-inset-bottom))" : 2.5,
          borderRadius: isMobile ? 0 : "12px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box display="flex" alignItems="center" gap={0.5} mb={1.5} flexShrink={0}>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700, fontSize: "1.125rem", letterSpacing: -0.2 }}>
            Nova Mensagem
          </Typography>
          <Tooltip title="Fechar">
            <IconButton onClick={handleCollapse} size="small" aria-label="Fechar">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {emailSendError ? (
          <Alert severity="warning" onClose={onDismissEmailError} sx={{ mb: 1.5 }}>
            {emailSendError}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1.25 : 2,
            alignItems: "stretch",
            flex: 1,
            minHeight: 0,
          }}
        >
          {showCustomerPane ? (
            <CustomerEmailPane
              emails={customerEmails}
              showAll={showAllCustomerEmails}
              onToggleAll={() => setShowAllCustomerEmails((open) => !open)}
              onHide={toggleCustomerMessage}
              isMobile={isMobile}
            />
          ) : null}

          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {customerEmail && customerMessageHidden ? (
          <Button
            size="small"
            onClick={toggleCustomerMessage}
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              color: "text.secondary",
              mb: 1,
              px: 0.5,
              flexShrink: 0,
            }}
          >
            Mostrar mensagem do cliente
          </Button>
        ) : null}
        <Box sx={{ mb: 1.5, flexShrink: 0 }}>
          <TextField
            fullWidth
            label="Para"
            placeholder="destinatário@email.com"
            variant="standard"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={{ "& .MuiInputBase-root": { borderBottom: fieldDivider, pb: 0.5, fontSize: "0.95rem" } }}
          />
          <TextField
            fullWidth
            label="CC"
            placeholder="opcional, separar por vírgula"
            variant="standard"
            value={emailCc}
            onChange={(e) => setEmailCc(e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={{ mt: 1, "& .MuiInputBase-root": { borderBottom: fieldDivider, pb: 0.5, fontSize: "0.95rem" } }}
          />
          <TextField
            fullWidth
            label="Assunto"
            placeholder="Assunto do e-mail"
            variant="standard"
            value={emailSubject}
            onChange={(e) => {
              subjectEditedRef.current = true;
              onDismissEmailError?.();
              setEmailSubject(e.target.value);
            }}
            InputProps={{ disableUnderline: true }}
            sx={{ mt: 1, "& .MuiInputBase-root": { borderBottom: fieldDivider, pb: 0.5, fontSize: "0.95rem" } }}
          />
        </Box>


          <Box
            sx={{
              flex: 1,
              minHeight: isMobile ? 200 : 280,
              display: "flex",
              flexDirection: "column",
              border: fieldDivider,
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "background.paper",
              "& .quill": { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 },
              "& .ql-toolbar": {
                border: "none",
                borderBottom: fieldDivider,
                bgcolor: theme.palette.mode === "dark" ? "action.hover" : "grey.50",
              },
              "& .ql-container": { flex: 1, minHeight: isMobile ? 140 : 240, fontSize: "1rem", border: "none" },
              "& .ql-editor": {
                minHeight: isMobile ? 140 : 240,
                fontSize: "1rem",
                lineHeight: 1.6,
              },
            }}
          >
          <ReactQuill
          value={toQuillHtml(message)}
          onChange={(value, _delta, source) => {
            // O Quill dispara um documento vazio ao montar e apagava o texto
            // já escrito. Esse primeiro evento não pode substituir o corpo.
            if (!quillHydratedRef.current) {
              quillHydratedRef.current = true;
              if (!composerHasTextPayload(value)) return;
            }
            if (source !== "user") return;
            onDismissEmailError?.();
            setMessage(value);
          }}
          theme="snow"
          placeholder="Escreva sua mensagem aqui..."
        />
          </Box>
        
        {(files.length ? files : file ? [file] : []).length > 0 ? (
          <Box sx={{ maxHeight: 132, overflow: "auto", flexShrink: 0, mt: 1 }}>
            {(files.length ? files : file ? [file] : []).map((f, index) => (
              <FilePreview
                key={`${f?.name}-${f?.size}-${index}`}
                file={f}
                onRemove={() => handleRemoveFile(index)}
              />
            ))}
          </Box>
        ) : null}

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          mt={1.5}
          pt={1.25}
          flexShrink={0}
          sx={{ borderTop: fieldDivider }}
        >
          <Tooltip title="Anexar arquivo">
            <IconButton onClick={openFilePicker} aria-label="Anexar arquivo">
              <AttachFileIcon />
            </IconButton>
          </Tooltip>
          <Box display="flex" gap={1}>
          <Button
            variant="text"
            onClick={handleCollapse}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSend}
            disabled={!canSend}
            sx={{ textTransform: "none", px: 2.5, boxShadow: "none" }}
          >
            {emailSending ? "Enviando..." : "Enviar"}
          </Button>
          </Box>
        </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
      )}
    </Box>
  );
};

export default MessageEmailChannel;
